import { getLogicalExpression, getFilterExpression } from '.';
import { axios } from '../internal';
import { CollectionType, IdentityAvatarType, IdentityType, UserType } from './types';

export type IdentityDataType = 'usersOnly' | 'groupsOnly' | 'usersAndGroups';

const CURRENT_USER_ID = '@currentUser';

const getBaseUrl = (id: string, type: UserType) => {
  return `/identities/${type === 'user' ? 'users' : 'groups'}/${id}`;
};

/**
 * @returns a Promise that resolves to the identity info about the given id and type.
 */
export const getIdentity = async (id: string = CURRENT_USER_ID, type: UserType = 'user') => {
  const { data } = await axios.get<IdentityType>(getBaseUrl(id, type), {
    headers: {
      Accept: `application/vnd.sas.identity.${type}+json`,
    },
  });
  return data;
};

/**
 * @returns a Promise that resolves to the avatar info about the given id and type.
 */
export const getIdentityAvatar = async (id: string = CURRENT_USER_ID, type: UserType = 'user') => {
  const { data } = await axios.get<IdentityAvatarType>(`${getBaseUrl(id, type)}/avatar`, {
    headers: {
      Accept: 'application/vnd.sas.identity.avatar+json',
    },
  });
  return data;
};

/**
 * @returns the <code>src</code> of the avatar's custom image.
 */
export const getIdentityAvatarSrc = (id: string = CURRENT_USER_ID, type: UserType = 'user') =>
  `${getBaseUrl(id, type)}/avatar/content`;

const createFilterString = (filterValue: string, idFilterValue?: string) => {
  if (idFilterValue === undefined) {
    return filterValue.length > 0
      ? `&filter=${getLogicalExpression(
          'or',
          getFilterExpression('contains', '$primary,name', filterValue),
          getFilterExpression('contains', '$primary,id', filterValue)
        )}`
      : '';
  }
  const nameFilterValue = filterValue.split('(').slice(0, -1).join('(').trim();
  return `&filter=${getLogicalExpression(
    'or',
    getFilterExpression('contains', '$primary,name', filterValue),
    getLogicalExpression(
      'and',
      getFilterExpression('contains', '$primary,name', nameFilterValue),
      getFilterExpression('startsWith', '$primary,id', idFilterValue)
    )
  )}`;
};
/**
 * @param filterValue Only the identities, of which the id or the name contains this value, would be returned.
 * @param idFilterValue optional extra filter value to allow name and id to be filtered differently.  Falls back on filterValue.
 * @returns a Promise that resolves to a collection of identities.
 */
export const getIdentityList = async (
  startIndex: number,
  limit: number,
  filterValue: string,
  dataType: IdentityDataType,
  idFilterValue?: string
): Promise<CollectionType<IdentityType>> => {
  const filter = createFilterString(filterValue, idFilterValue);
  const getType = () => {
    switch (dataType) {
      case 'usersOnly':
        return 'users';
      case 'groupsOnly':
        return 'groups';
      case 'usersAndGroups':
        return 'identities';
    }
  };

  const url = `/identities/${getType()}?start=${startIndex}&limit=${limit}&sortBy=name%3Aprimary${filter}&_=${Date.now()}`;

  try {
    const { data } = await axios.get<CollectionType<IdentityType>>(url, {
      headers: {
        Accept: 'application/vnd.sas.collection+json',
      },
    });
    return data;
  } catch (e) {
    throw new Error(`Failed to fetch user/group list ${url}`);
  }
};

/**
 * @param filterValues Array of ids to retrieve identities for.
 * @returns a Promise that resolves to a collection of identities.
 */
export const getIdentityListByIds = async (
  startIndex: number,
  limit: number,
  filterValues: string[],
  dataType: Exclude<IdentityDataType, 'usersAndGroups'>
): Promise<CollectionType<IdentityType>> => {
  const filter =
    filterValues.length > 0 ? `&filter=${getFilterExpression('in', 'id', ...filterValues)}` : '';
  const type = dataType === 'usersOnly' ? 'users' : 'groups';
  const url = `/identities/${type}?start=${startIndex}&limit=${limit}&sortBy=name%3Aascending${filter}&_=${Date.now()}`;
  try {
    const { data } = await axios.get<CollectionType<IdentityType>>(url, {
      headers: {
        Accept: 'application/vnd.sas.collection+json',
      },
    });
    return data;
  } catch (e) {
    throw new Error(`Failed to fetch user/group list by id ${url}`);
  }
};

export const saveAvatar = async (
  contentType: string,
  body: Blob,
  id: string = CURRENT_USER_ID,
  type: UserType = 'user'
): Promise<void> => {
  await axios.put<void>(`${getBaseUrl(id, type)}/avatar`, body, {
    headers: {
      Accept: 'application/vnd.sas.identity.avatar+json',
      'Content-Type': contentType,
    },
  });
};

export const deleteAvatar = async (
  id: string = CURRENT_USER_ID,
  type: UserType = 'user'
): Promise<void> => {
  await axios.delete<void>(`${getBaseUrl(id, type)}/avatar`, {
    headers: {
      Accept: 'application/vnd.sas.identity.avatar+json',
    },
  });
};

export const hasElevatedPrivileges = async (
  id: string = CURRENT_USER_ID,
  type: UserType = 'user'
): Promise<boolean> => {
  const { data } = await axios.get(`${getBaseUrl(id, type)}/administrator`, {
    headers: {
      Accept: 'application/vnd.sas.primitive.boolean+json',
    },
  });
  return data;
};

/**
 * @param filterValues Array of ids to retrieve identities for.
 * @returns a Promise that resolves to a collection of identities.
 */
export const getFilterIdentityListByIds = async (
  filterValues: string[],
  dataType: Exclude<IdentityDataType, 'usersAndGroups'>
): Promise<CollectionType<IdentityType>> => {
  const filter =
    filterValues.length > 0 ? `filter=${getFilterExpression('in', 'id', ...filterValues)}` : '';
  const type = dataType === 'usersOnly' ? 'users' : 'groups';
  const url = `/identities/${type}?${filter}`;
  try {
    const { data } = await axios.get<CollectionType<IdentityType>>(url, {
      headers: {
        Accept: 'application/vnd.sas.collection+json',
      },
    });
    return data;
  } catch (e) {
    throw new Error(`Failed to fetch user/group list by id ${url}`);
  }
};
